import { isStagingEnvironment } from "../util/features";

// Axios interceptors run outside React, so this module flag lets the request guard
// synchronously know whether the current global admin has switched into edit mode.
let _isEditMode = false;
// Axios interceptors run outside React, so this module flag lets the request guard
// synchronously know whether edit-mode protections apply for the current user.
let _shouldEnforceEditMode = false;

type BlockedListener = () => void;

const blockedListeners = new Set<BlockedListener>();
const EDIT_MODE_STORAGE_KEY = "polaris.globalAdminEditMode";
const DEFAULT_EDIT_MODE_TTL_MS = 15 * 60 * 1000; // 15 min
const EXTENDED_EDIT_MODE_TTL_MS = 3 * 60 * 60 * 1000; // 3 hours

export class EditModeBlockedError extends Error {
  isEditModeBlocked = true;

  constructor() {
    super("Request blocked while in read-only mode.");
    this.name = "EditModeBlockedError";
  }
}

export const isEditModeBlockedError = (
  error: unknown,
): error is EditModeBlockedError & { isEditModeBlocked: true } => {
  return Boolean(
    error &&
      typeof error === "object" &&
      "isEditModeBlocked" in error &&
      (error as { isEditModeBlocked?: unknown }).isEditModeBlocked === true,
  );
};

export const setEditModeFlag = (value: boolean) => {
  _isEditMode = value;
};

export const getEditModeFlag = (): boolean => _isEditMode;

export const getStoredEditModeExpiresAt = (): number | null => {
  try {
    const expiresAt = Number(window.localStorage.getItem(EDIT_MODE_STORAGE_KEY));

    if (!Number.isFinite(expiresAt) || Date.now() >= expiresAt) {
      window.localStorage.removeItem(EDIT_MODE_STORAGE_KEY);
      return null;
    }

    return expiresAt;
  } catch {
    return null;
  }
};

export const getStoredEditModeFlag = (): boolean => {
  return getStoredEditModeExpiresAt() !== null;
};

export const shouldUseExtendedEditMode = (): boolean => {
  return isStagingEnvironment() || window.location.host === "localhost:5000";
};

export const getEditModeTtlMs = (): number => {
  return shouldUseExtendedEditMode() ? EXTENDED_EDIT_MODE_TTL_MS : DEFAULT_EDIT_MODE_TTL_MS;
};

export const getEditModeDurationLabel = (): string => {
  return shouldUseExtendedEditMode() ? "3 hours" : "15 minutes";
};

export const storeEditModeFlag = (value: boolean, expiresAt = Date.now() + getEditModeTtlMs()) => {
  try {
    if (value) {
      window.localStorage.setItem(EDIT_MODE_STORAGE_KEY, String(expiresAt));
    } else {
      window.localStorage.removeItem(EDIT_MODE_STORAGE_KEY);
    }
  } catch {
    // Ignore localStorage errors so edit-mode guarding still works in-memory.
  }
};

export const clearEditModeFlag = () => {
  setEditModeFlag(false);
  storeEditModeFlag(false);
};

export const getNewEditModeExpiresAt = (): number => Date.now() + getEditModeTtlMs();

export const getRemainingEditModeMs = (): number => {
  const expiresAt = getStoredEditModeExpiresAt();

  if (!expiresAt) {
    return 0;
  }

  return Math.max(0, expiresAt - Date.now());
};

export const setShouldEnforceEditMode = (value: boolean) => {
  _shouldEnforceEditMode = value;
};

export const getShouldEnforceEditMode = (): boolean => _shouldEnforceEditMode;

export const subscribeToBlockedActions = (listener: BlockedListener) => {
  blockedListeners.add(listener);
  return () => {
    blockedListeners.delete(listener);
  };
};

export const onBlocked = (force = false) => {
  if (!_shouldEnforceEditMode && !force) {
    return;
  }

  [...blockedListeners].forEach((listener) => listener());
};
